import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useStore } from '@/shared/store/store-context';

import { useDayFormat } from '../hooks/use-day-format';
import {
  MAX_REPORT_DAYS,
  RANGE_PRESETS,
  rangeProblem,
  type RangePreset,
  type RangeSearch,
  type ResolvedRange,
} from '../schemas/report-range';

export interface RangePickerProps {
  range: ResolvedRange;
  onChange: (next: RangeSearch) => void;
}

/**
 * Preset select plus, for "custom", two day inputs applied together. A custom range the backend
 * would refuse (end before start, more than 366 days) is never written to the URL.
 */
export function RangePicker({ range, onChange }: RangePickerProps) {
  const { t } = useTranslation('reports');
  const days = useDayFormat();
  // Report days are the store's calendar days, so "Today" in the pickers is the store's today.
  const { timezone } = useStore();
  const [custom, setCustom] = useState(range.preset === 'custom');
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [touched, setTouched] = useState(false);
  const problem = rangeProblem(from, to);

  const selectPreset = (value: RangePreset) => {
    if (value === 'custom') {
      setCustom(true);
      setFrom(range.from);
      setTo(range.to);
      setTouched(false);
      return;
    }
    setCustom(false);
    onChange({ range: value, from: undefined, to: undefined });
  };

  const apply = () => {
    setTouched(true);
    if (problem === null) onChange({ range: 'custom', from, to });
  };

  return (
    <div className="flex flex-col gap-2 pb-4" data-tour="reports.range">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-range" className="text-muted-foreground text-xs">
            {t('range.label')}
          </Label>
          <Select value={custom ? 'custom' : range.preset} onValueChange={selectPreset}>
            <SelectTrigger id="report-range" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((preset) => (
                <SelectItem key={preset} value={preset}>
                  {t(`range.presets.${preset}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {custom ? (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="report-from" className="text-muted-foreground text-xs">
                {t('range.from')}
              </Label>
              <DatePicker
                id="report-from"
                value={from}
                onChange={setFrom}
                timeZone={timezone}
                className="w-[160px]"
                aria-invalid={touched && problem !== null}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="report-to" className="text-muted-foreground text-xs">
                {t('range.to')}
              </Label>
              <DatePicker
                id="report-to"
                value={to}
                onChange={setTo}
                timeZone={timezone}
                className="w-[160px]"
                aria-invalid={touched && problem !== null}
                aria-describedby={touched && problem ? 'report-range-error' : undefined}
              />
            </div>
            <Button type="button" variant="outline" onClick={apply}>
              {t('range.apply')}
            </Button>
          </>
        ) : null}
        <span className="text-muted-foreground pb-2 text-sm" data-testid="report-range-label">
          {days.range(range.from, range.to)}
        </span>
      </div>
      {custom && touched && problem ? (
        <p id="report-range-error" role="alert" className="text-destructive text-sm">
          {t(`range.problems.${problem}`, { max: MAX_REPORT_DAYS })}
        </p>
      ) : null}
    </div>
  );
}
